import { ComponentType } from "react";
import { AppContext } from "App";

import { ClientType, IClientFactory } from "@multiweb/clients/index";
import { ISectionLoggerFactory, LoggingSection, LoggingSectionType } from "@multiweb/logging";
import {
    AvatarComponent,
    ButtonFake,
    CheckBox as ReactCheckBox,
    ComboBox as ReactComboBox,
    Edit as ReactEdit,
    InputFake,
    LinkButton,
    LinkEditFake,
    MemoFake,
    NumEdit as ReactNumEdit,
    PasswordEdit as ReactPasswordEdit,
    ReportingScalar,
    RichTextEdit,
} from "@multiweb/reactComponents";
import { AvatarConnector } from "@multiweb/reactConnectors/AvatarConnector";
import { CheckBoxControlConnector } from "@multiweb/reactConnectors/CheckBoxControlConnector";
import { ComboBoxControlConnector } from "@multiweb/reactConnectors/ComboBoxControlConnector";
import { ControlConnector } from "@multiweb/reactConnectors/ControlConnector";
import { EditableControlConnector } from "@multiweb/reactConnectors/EditableControlConnector";
import { IconEditConnector } from "@multiweb/reactConnectors/IconEditConnector";
import { LinkButtonConnector } from "@multiweb/reactConnectors/LinkButtonConnector";
import { NumEditConnector } from "@multiweb/reactConnectors/NumEditConnector";
import { ReportingScalarConnector } from "@multiweb/reactConnectors/ReportingScalarConnector";
import { RichTextEditConnector } from "@multiweb/reactConnectors/RichTextEditConnector";
import { ITypeFactory } from "Global/Base/DIContainer";
import { StoreOriginalPrototypeMixin } from "Global/Base/Mixins/StoreOriginalPrototypeMixin";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { NoEditMultiEditMixin } from "UI/UI.Controls/mixins/noEditMixin";
import { IconEdit } from "UI/UI.React/components/IconEdit";
import { DIContainer } from "../../../Global/Base/DIContainer";
import { CursorValueAdjustableConnectorMixin } from "../../UI.Controls/mixins/cursorValueAdjustableMixin";
import { MultiEditMixin } from "../../UI.Controls/mixins/multiEditMixin";
import { numEditFactory } from "../../UI.ElementarControls/NumEditFactory";
import { UnsupportedComponent } from "../../UI.ElementarControls/UnsupportedComponent";
import { GridLayoutConnector } from "../../UI.ReactControlConnectors/GridLayoutConnector";
import { LiteListFrameConnector } from "../../UI.ReactControlConnectors/LiteListFrameConnector";
import { ColorEdit } from "../UI/Controls/ColorEdit";
import { ContainerControl } from "../UI/Controls/ContainerControl";
import { Control } from "../UI/Controls/Control";
import { ControlWithLoggerParams, FormControlParams, IControl } from "../UI/Controls/ControlInterfaces";
import { DateEdit } from "../UI/Controls/DateEdit";
import { DateTimeEdit } from "../UI/Controls/DateTimeEdit2";
import { FileEdit } from "../UI/Controls/FileEdit";
import { FlagEdit } from "../UI/Controls/FlagEdit";
import { GroupBox } from "../UI/Controls/GroupBox";
import { ImageEdit } from "../UI/Controls/ImageEdit";
import { Label } from "../UI/Controls/Label";
import { LinkEdit } from "../UI/Controls/LinkEdit";
import { LOVEdit } from "../UI/Controls/LOVEdit";
import { Memo } from "../UI/Controls/Memo";
import { RadioGroup } from "../UI/Controls/RadioGroup";
import { IFactoryMethodSignature, Registrator } from "../UI/Controls/Registrator";
import { SimpleLabel } from "../UI/Controls/SimpleLabel";
import { TextLOVEdit } from "../UI/Controls/TextLOVEdit";
import { TimeEdit } from "../UI/Controls/TimeEdit";
import { WorkflowEditor } from "../UI/Controls/WorkflowEditor";
import { CryptoAction } from "../UI/XGControls/Actions/CryptoAction";
import { CryptoButton } from "../UI/XGControls/Actions/CryptoButton";
import { CryptoLabel } from "../UI/XGControls/Actions/CryptoLabel";
import { CryptoLabelAction } from "../UI/XGControls/Actions/CryptoLabelAction";
import { DetailFormAction } from "../UI/XGControls/Actions/DetailFormAction";
import { FormButton } from "../UI/XGControls/Actions/FormButton";
import { MethodAction } from "../UI/XGControls/Actions/MethodAction";
import { MethodButton } from "../UI/XGControls/Actions/MethodButton";
import { NodeAction } from "../UI/XGControls/Actions/NodeAction";
import { ParametricFormAction } from "../UI/XGControls/Actions/ParametricFormAction";
import { ParametricFormButton } from "../UI/XGControls/Actions/ParametricFormButton";
import { ReportAction } from "../UI/XGControls/Actions/ReportAction";
import { ShowInsertAction } from "../UI/XGControls/Actions/ShowInsertAction";
import { WFStaticAction } from "../UI/XGControls/Actions/WFStaticAction";
import { AlignPanel } from "../UI/XGControls/AlignPanel";
import { ExportPanel } from "../UI/XGControls/Controls/ExportPanel";
import { ImportPanel } from "../UI/XGControls/Controls/ImportPanel";
import { DataEventLabel } from "../UI/XGControls/DataEventLabel";
import { DataLabel } from "../UI/XGControls/DataLabel";
import { DetailFrame } from "../UI/XGControls/DetailFrame";
import { Form } from "../UI/XGControls/Form";
import { CalendarFrame } from "../UI/XGControls/Frames/CalendarFrame";
import { ChartFrame } from "../UI/XGControls/Frames/ChartFrame";
import { CommentaryFrame } from "../UI/XGControls/Frames/CommentaryFrame";
import { ListFrame } from "../UI/XGControls/Frames/ListFrame";
import { TreeFrame } from "../UI/XGControls/Frames/TreeFrame";
import { TreeViewFrame } from "../UI/XGControls/Frames/TreeViewFrame";
import { TwoListFrame } from "../UI/XGControls/Frames/TwoListFrame";
import { InfoLabel } from "../UI/XGControls/InfoLabel";
import { FormInternalPanel } from "../UI/XGControls/Internal/FormInternalPanel";
import { MSLOV } from "../UI/XGControls/MSLOV";
import { ObjectListEdit } from "../UI/XGControls/ObjectListEdit";
import { PdfEdit } from "../UI/XGControls/PDFEdit/PdfEdit";
import { SeparateDetailFrame } from "../UI/XGControls/SeparateDetailFrame";
import { Pages } from "../UI/XGControls/TabPages";
import { VerticalScrollViewer } from "../UI/XGControls/VerticalScrollViewer";
import { WidgetPanel } from "../UI/XGControls/WidgetPanel";
import { WindowedDetailFrame } from "../UI/XGControls/WindowedDetailFrame";
import { WrapPanel } from "../UI/XGControls/WrapPanel";
import { ChangePasswordSettingsContainer } from "./ApplicationSettings/ChangePasswordSettings";
import { LanguageSettings } from "./ApplicationSettings/LanguageSettings";
import { RolesRangesSettingsContainer } from "./ApplicationSettings/RolesRangesSettings";
import { UserInfoSettings } from "./ApplicationSettings/UserInfoSettings";

/**
 * Pomocná třída udržující standardní definice továrních metod pro vytváření jednotlivých komponent
 * @internal
 */
export class ComponentDefinitions {
    private static _registered = false;

    /**
     * Zaregistruje pomocí třídy Registrator tovární metod\ pro vytváření jednotlivých komponent
     */
    public static registerStandardDefinitions(di: ITypeFactory): void {
        if (ComponentDefinitions._registered)
            return;

        ComponentDefinitions._registered = true;

        const reg: typeof Registrator = Registrator;

        reg.add("VerticalScrollViewer", VerticalScrollViewer);
        reg.add("WrapPanel", WrapPanel);
        reg.add("WidgetPanel", WidgetPanel);
        reg.add("Pages", Pages);
        reg.add("AlignPanel", AlignPanel);
        reg.add("SplitPanel", Control); // musí mít nějaký control, aby nad ním šlo vyhodnocovat práva, i když logika je v custom elementu
        reg.add("GroupBox", GroupBox);
        reg.add("WorkflowEditor", WorkflowEditor);

        reg.add("FlagEdit", StoreOriginalPrototypeMixin(MultiEditMixin(FlagEdit, InputFake), FlagEdit));
        reg.add("RadioGroup", _withLogger(RadioGroup, LoggingSection.radioGroup));
        reg.add("FileEdit", StoreOriginalPrototypeMixin(NoEditMultiEditMixin(FileEdit, ButtonFake), FileEdit));
        reg.add("ImageEdit", StoreOriginalPrototypeMixin(NoEditMultiEditMixin(ImageEdit, ButtonFake), ImageEdit));
        reg.add("PDFEdit", (params: FormControlParams) => {
            const PdfEditClass = StoreOriginalPrototypeMixin(NoEditMultiEditMixin(PdfEdit, ButtonFake), PdfEdit);

            return new PdfEditClass({
                ...params,
                userName: AppContext.instance.currentUser.fullName,
                previewFileSizeLimit: AppContext.instance.configuration.modules?.pdfEditor?.previewFileSizeLimit,
            });
        });
        reg.add("DateEdit", StoreOriginalPrototypeMixin(MultiEditMixin(DateEdit, InputFake), DateEdit));
        reg.add("DateTimeEdit", StoreOriginalPrototypeMixin(MultiEditMixin(DateTimeEdit, InputFake), DateTimeEdit));
        reg.add("TimeEdit", StoreOriginalPrototypeMixin(MultiEditMixin(TimeEdit, InputFake), TimeEdit));
        reg.add("LinkEdit", StoreOriginalPrototypeMixin(MultiEditMixin(LinkEdit, LinkEditFake), LinkEdit));
        reg.add("Memo", _withLogger(StoreOriginalPrototypeMixin(MultiEditMixin(Memo, MemoFake), Memo), LoggingSection.memo));
        reg.add("RichTextEdit", _withConnector(RichTextEditConnector, RichTextEdit));

        reg.add("DataLabel", _withLogger(DataLabel, LoggingSection.dataLabel));
        reg.add("InfoLabel", _withLogger(InfoLabel, LoggingSection.infoLabel));
        reg.add("LOVEdit", (params: FormControlParams) => {
            const LovEditClass = StoreOriginalPrototypeMixin(MultiEditMixin(LOVEdit, InputFake), LOVEdit);
            return new LovEditClass({ ...params, defaultWhisperer: AppContext.instance.configuration.defaultWhisperer });
        });
        reg.add("IconEdit", _withConnector(IconEditConnector, IconEdit));
        reg.add("TextLOVEdit", (params: FormControlParams) => {
            const TextLOVEditClass = StoreOriginalPrototypeMixin(MultiEditMixin(TextLOVEdit, InputFake), TextLOVEdit);
            const logger = DIContainer.instance.create(ISectionLoggerFactory).create(LoggingSection.textLOVEdit);
            return new TextLOVEditClass({ ...params, dataReaderClient: di.create(IClientFactory).get(ClientType.dataReader), logger });
        });
        reg.add("ColorEdit", StoreOriginalPrototypeMixin(MultiEditMixin(ColorEdit, InputFake), ColorEdit));
        reg.add("ReportingScalar", _withConnector(ReportingScalarConnector, ReportingScalar));
        reg.add("NumEdit", _withConnector(NumEditConnector, ReactNumEdit));
        reg.add("Edit", _withConnector(EditableControlConnector, ReactEdit));
        reg.add("CheckBox", _withConnector(CheckBoxControlConnector, ReactCheckBox));
        reg.add("ComboBox", _withConnector(ComboBoxControlConnector, ReactComboBox));
        reg.add("PasswordEdit", _withConnector(CursorValueAdjustableConnectorMixin(EditableControlConnector), ReactPasswordEdit));
        reg.add("NumEditColumn", (params) => numEditFactory.create(params));

        reg.add("ExportPanel", _withLogger(ExportPanel, LoggingSection.exportImportFrame));
        reg.add("ImportPanel", _withLogger(ImportPanel, LoggingSection.exportImportFrame));

        //akce
        reg.add("DetailFormAction", DetailFormAction);
        reg.add("MethodAction", MethodAction);
        reg.add("WFAction", WFStaticAction);
        reg.add("CryptoAction", CryptoAction);
        reg.add("CryptoLabelAction", CryptoLabelAction);
        reg.add("ReportAction", ReportAction);
        reg.add("NodeAction", NodeAction);
        reg.add("ParametricFormAction", ParametricFormAction);
        reg.add("ShowInsertAction", ShowInsertAction);

        reg.add("Label", Label);
        reg.add("MethodButton", MethodButton);
        reg.add("CryptoButton", StoreOriginalPrototypeMixin(NoEditMultiEditMixin(CryptoButton, ButtonFake), CryptoButton));
        reg.add("CryptoLabel", StoreOriginalPrototypeMixin(NoEditMultiEditMixin(CryptoLabel, ButtonFake), CryptoLabel));
        reg.add("ParametricFormButton", StoreOriginalPrototypeMixin(NoEditMultiEditMixin(ParametricFormButton, ButtonFake), ParametricFormButton));
        reg.add("FormButton", StoreOriginalPrototypeMixin(NoEditMultiEditMixin(FormButton, ButtonFake), FormButton));
        reg.add("ObjectListEdit", _withLogger(ObjectListEdit, LoggingSection.objectListEdit));
        reg.add("DataEventLabel", (params: FormControlParams) => {
            const logger = DIContainer.instance.create(ISectionLoggerFactory).create(LoggingSection.dataEventLabel);
            return new DataEventLabel({ ...params, dataEventsManager: di.create(IDataEventsManager), logger });
        });

        //seznamy
        reg.add("TreeViewFrame", _withLogger(TreeViewFrame, LoggingSection.treeViewFrame));
        reg.add("CalendarFrame", _withLogger(CalendarFrame, LoggingSection.calendarFrame));
        reg.add("TreeFrame", _withLogger(TreeFrame, LoggingSection.treeFrame));
        reg.add("ListFrame", ListFrame);
        reg.add("CommentaryFrame", _withLogger(CommentaryFrame, LoggingSection.commentaryFrame));
        reg.add("TwoListFrame", _withLogger(TwoListFrame, LoggingSection.twoListFrame));

        reg.add("DetailFrame", (params: FormControlParams) => {
            const { ownerForm: form } = params;
            if (form && !form.isModal && form.isDetailForm) {
                return new WindowedDetailFrame(params);
            }
            return new DetailFrame(params);
        });

        reg.add("SeparateDetailFrame", _withLogger(SeparateDetailFrame, LoggingSection.separateDetailFrame));
        reg.add("ChartFrame", ChartFrame);
        reg.add("MultiSelectLOV", _withLogger(MSLOV, LoggingSection.mslov));
        reg.add("Form", Form);
        reg.add("FormInternalPanel", FormInternalPanel);

        reg.add("UnsupportedComponent", (params: FormControlParams) => {
            return new ContainerControl({
                elementCreator: UnsupportedComponent,
                copyAttrs: new Map<string, string>([["data-unsupported-type", "data-unsupported-type"]]),
                parentElement: params.element,
                originalRightsDefinition: params.originalRightsDefinition,
            });
        });

        reg.add("GridLayout", GridLayoutConnector);
        reg.add("LiteListFrame", _withLogger(LiteListFrameConnector, LoggingSection.liteListFrame));
        reg.add("SimpleLabel", SimpleLabel);

        reg.add("LinkButton", _withConnector(LinkButtonConnector, LinkButton));
        reg.add("RolesRangesSettings", RolesRangesSettingsContainer);
        reg.add("ChangePasswordSettings", ChangePasswordSettingsContainer);
        reg.add("LanguageSettings", LanguageSettings);
        reg.add("UserInfoSettings", UserInfoSettings);

        reg.add("Avatar", _withConnector(AvatarConnector, AvatarComponent));

        this._registerToggleableComponents(reg);
    }

    /**
     * Pomocná metoda pro registraci přepínatelných komponent.
     * Přepínatelná komponenta je v tomto případě ta, jenž je implementována jak v původní aplikaci, tak i v reactu.
     * Přepínání je umožněno na základě experimentální funkcionality `experiments` v rozhraní {@link IInitParams}.
     * Metoda zaregistruje pomocí třídy Registrator tovární metody pro vytváření jednotlivých komponent.
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private static _registerToggleableComponents(_reg: typeof Registrator): void {
    }
}

/** Předpis konstruktoru pro vytváření controls s loggerem. */
interface IControlWithLoggerConstructorSignature {
    new(params: ControlWithLoggerParams): IControl;
}

/**
 * Vrací novou {@link IFactoryMethodSignature}, která instancuje `controlClass` i s loggerem.
 *
 * @param controlClass Třída, jejíž instance bude vrácená funkce vytvářet.
 * @param loggingSection Oblast, pro kterou bude logovat příslušný logger.
 */
const _withLogger = (controlClass: IControlWithLoggerConstructorSignature, loggingSection: LoggingSectionType): IFactoryMethodSignature => {
    return ((params: FormControlParams) => {
        const logger = DIContainer.instance.create(ISectionLoggerFactory).create(loggingSection);
        const paramsWithLogger = { ...params, logger };
        return new controlClass(paramsWithLogger);
    });
};

/**
 * Vrací novou instanci {@link ConstructorFunction}, která instancuje `connector` s definovanou React komponentou.
 * Tuto komponentu v rámci konektoru defaultně nastavuje jako nekompozitní.
 *
 * @param connector Třída, jejíž instance bude vrácená funkce vytvářet.
 * @param component React komponenta, která je skrze `connector` propojena s původní aplikací.
 */
const _withConnector = <T>(connector: typeof ControlConnector, component: ComponentType<T>) => (
    (params: FormControlParams) => {
        const { element: rootElement, originalRightsDefinition } = params;
        return new connector({
            componentId: rootElement.getAttribute("data-componentId") || undefined,
            component,
            isCompositeComponent: false,
            rootElement,
            originalRightsDefinition,
        });
    }
);
